import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { getSettings, saveSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Save, X, Plus, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [weekly, setWeekly] = useState(true);
  const [minScore, setMinScore] = useState(4);
  const [areaFocus, setAreaFocus] = useState(true);

  useEffect(() => {
    if (data) {
      setRecipients(data.recipients || []);
      setWeekly(data.weekly_enabled);
      setMinScore(data.min_score_for_digest);
      setAreaFocus(data.area_focus !== false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => saveSettings({ recipients, weekly_enabled: weekly, min_score_for_digest: minScore, area_focus: areaFocus }),
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: () => toast.error("Save failed"),
  });

  const addEmail = () => {
    const e = newEmail.trim();
    if (e && /\S+@\S+\.\S+/.test(e) && !recipients.includes(e)) {
      setRecipients([...recipients, e]);
      setNewEmail("");
    } else {
      toast.error("Enter a valid, unique email");
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Settings</h1>
        <p className="text-sm text-zinc-500 mt-2">Configure who receives the weekly digest and how it's filtered.</p>
      </div>

      <div className="border-2 border-zinc-950 bg-white p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-[#2F6DA3]" />
          <h2 className="font-display text-xl font-bold tracking-tight">Digest Recipients</h2>
        </div>
        <div className="space-y-2">
          {recipients.map((e) => (
            <div key={e} className="flex items-center justify-between border-2 border-zinc-200 px-3 py-2" data-testid={`recipient-${e}`}>
              <span className="text-sm font-mono">{e}</span>
              <button onClick={() => setRecipients(recipients.filter((x) => x !== e))} data-testid={`remove-${e}`} className="text-zinc-400 hover:text-red-600">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()}
            placeholder="add@email.com" data-testid="new-email-input" className="border-2 border-zinc-950" />
          <Button onClick={addEmail} data-testid="add-email-button" variant="outline" className="border-2 border-zinc-950 font-bold uppercase tracking-wider">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="border-2 border-zinc-950 bg-white p-6 space-y-5">
        <h2 className="font-display text-xl font-bold tracking-tight">Schedule & Filtering</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-sm">Automatic weekly send</div>
            <div className="text-xs text-zinc-500">Sends every Monday at 07:00 UTC after an auto-scan.</div>
          </div>
          <Switch checked={weekly} onCheckedChange={setWeekly} data-testid="weekly-toggle" />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-sm">Focus on my areas only</div>
            <div className="text-xs text-zinc-500">Limit the email to North East, Scotland, Norwich, Watford & UK-wide (National) frameworks.</div>
          </div>
          <Switch checked={areaFocus} onCheckedChange={setAreaFocus} data-testid="area-focus-toggle" />
        </div>
        <div>
          <div className="font-bold text-sm mb-2">Minimum score to include</div>
          <div className="flex gap-2">
            {[3, 4, 5].map((s) => (
              <button key={s} onClick={() => setMinScore(s)} data-testid={`settings-minscore-${s}`}
                className={`w-10 h-10 text-sm font-bold border-2 transition-colors ${minScore === s ? "bg-[#2F6DA3] text-white border-[#2F6DA3]" : "bg-white border-zinc-300 hover:border-zinc-950"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-settings-button" className="bg-zinc-950 text-white font-bold uppercase tracking-wider px-8 py-6">
        <Save size={16} className="mr-2" /> {save.isPending ? "Saving…" : "Save Settings"}
      </Button>
    </div>
  );
}
